import React, {useMemo, useState} from 'react';

import {
    Block,
    Button,
    Card,
    Dialog,
    DialogBody,
    DialogFooter,
    Icon,
    NoData,
    showInfo,
    showConfirm,
    Space,
    Text,
    Row,
    Col,
    VF,
    Modal,
    ModalComponentProps,
    Yup,
    makeValidate,
} from '@mc/ui-v4';
import {useQuery} from 'react-query';
import {Form, FormRenderProps} from 'react-final-form';
import {getDokumentZdrojItemsReadable} from '../../../enums/EnumTypLayerAttr';
import {useAtlasPristupnosti} from '../../../hooks/useTopicsMap';
import {getCiselnikInterpretaceItems, getStringInterpretaceItems, getTextInterpretaceItems} from '../../../enums/EnumInterpretace';
import {AtributyApi, AttributeConfigVO, AttributeExternalValueVO, CiselnikVO, EnumInterpretace, EnumTypLayerAttr, UserLayersApi} from '../../../api';
import {mcNew} from '@mc/react-ui-icons';
import {OnChange} from 'react-final-form-listeners';
import {CiselnikValueModal} from './CiselnikValueModal';
import {actionApiCall, WaitForQueryData} from '@mc/react-app-v4';
import {DefaultValueModal} from './DefaultValueModal';
import {CiselnikTree} from './CiselnikTree';
import {v4 as uuidv4} from 'uuid';

type Props = ModalComponentProps & {
    layerId: number;
    forbiddenAttributes: string[];
    initialValues?: AttributeConfigVO;
    isEdit: boolean;
    onSubmit: (data: AttributeConfigVO) => void;
    xsdTyp?: string;
};

const enumToValidate = {
    ciselnik: Yup.array().min(1, 'Číselník nesmí být prázdný'),
};
export const attributeSchema = (isEnum: boolean, forbiddenAttributes: string[]) =>
    Yup.object().shape({
        ...(isEnum && enumToValidate),
        popisek: Yup.string().required('Zadejte popisek').notOneOf(forbiddenAttributes),
        typ: Yup.string().required('Vyberte typ'),
    });

export const CreateUpdateAtributModal: React.FC<Props> = ({forbiddenAttributes, onClose, layerId, isEdit, initialValues, onSubmit, xsdTyp}) => {
    const [isEnum, setIsEnum] = useState(false);
    const atlasPristupnosti = useAtlasPristupnosti();
    const validate = useMemo(() => makeValidate(attributeSchema(isEnum, forbiddenAttributes)), [isEnum, forbiddenAttributes]);
    const typItems = useMemo(() => getDokumentZdrojItemsReadable(atlasPristupnosti), [atlasPristupnosti]);
    if (!isEdit) {
        initialValues = {
            publikovat: false,
            diskriminator: false,
        };
    }

    const externalAttributeQuery = useQuery({
        queryKey: ['externalAttribute', initialValues?.typ],
        queryFn: async () => (await AtributyApi.getExternalAtribut(xsdTyp)).data,
        enabled: !!xsdTyp,
    });

    const handleFormSubmit = async (data: AttributeConfigVO) => {
        if (!initialValues?.povinne && data.povinne) {
            const layerHasData = await UserLayersApi.getFeatureIds(layerId);
            if (layerHasData && layerHasData.data?.length > 0) {
                const result = await showConfirm({
                    title: 'Povinný atribut',
                    message: 'Chcete nastavit atribut jako povinný? Dojde k naplnění výchozí hodnotou pro všechny existující prvky, které ji nemají vyplněnou.',
                    intent: 'danger',
                });
                if (!result) return;

                if (data.defaultniHodnota) {
                    onSubmit(data);
                    return;
                }

                Modal.show(({onClose}) => (
                    <DefaultValueModal
                        onClose={onClose}
                        atribut={data}
                        onSubmit={async atData => {
                            data.defaultniHodnota = atData.hodnota;
                            onSubmit(data);
                            onClose();
                        }}
                    />
                ));
                return;
            }
        }
        onSubmit(data);
    };

    return (
        <Form<AttributeConfigVO> initialValues={{zobrazit: true, ...initialValues}} onSubmit={handleFormSubmit} validate={validate}>
            {({submitting, handleSubmit, values, form: {change}, submitFailed, errors}: FormRenderProps<AttributeConfigVO>) => {
                setIsEnum(values.typ === EnumTypLayerAttr.ENUM);
                //opens update modal, updates values
                const handleCreateUpdateHodnota = (initialValue?: CiselnikVO, index?: number) => {
                    const isEditHodnota = Number.isInteger(index);
                    const allValues: CiselnikVO[] = values.ciselnik ?? [];

                    // Nadřazenou hodnotou nesmí být hodnota sama ani její potomci (prevence cyklu).
                    const excludedTempIds = new Set<string>();
                    if (isEditHodnota && initialValue?.tempId) {
                        excludedTempIds.add(initialValue.tempId);
                        let changed = true;
                        while (changed) {
                            changed = false;
                            allValues.forEach(v => {
                                if (v.tempId && !excludedTempIds.has(v.tempId) && v.parentTempId && excludedTempIds.has(v.parentTempId)) {
                                    excludedTempIds.add(v.tempId);
                                    changed = true;
                                }
                            });
                        }
                    }
                    const parentOptions = allValues.filter(v => v.tempId && !excludedTempIds.has(v.tempId));

                    Modal.show(({onClose}) => (
                        <CiselnikValueModal
                            initialValues={initialValue}
                            isEdit={isEditHodnota}
                            parentOptions={parentOptions}
                            onSubmit={data => {
                                const ciselnikNewValue = values.ciselnik ? structuredClone(values.ciselnik) : [];
                                const node: CiselnikVO = {...data, parentTempId: data.parentTempId ?? undefined};
                                if (isEditHodnota) {
                                    // Zachovat stabilní klíč, serverové ID a kód hodnoty (uživatel mění jen název a rodiče)
                                    node.tempId = initialValue?.tempId ?? uuidv4();
                                    node.id = initialValue?.id;
                                    node.kod = initialValue?.kod;
                                    ciselnikNewValue[index] = node;
                                } else {
                                    node.tempId = uuidv4();
                                    node.id = undefined;
                                    ciselnikNewValue.push(node);
                                }
                                change('ciselnik', ciselnikNewValue);
                                onClose();
                            }}
                            onClose={onClose}
                        />
                    ));
                };

                //opens remove modal, updates values
                const removeHodnota = async (index: number) => {
                    const ciselnikValue = values.ciselnik[index];
                    // Nelze smazat hodnotu, která má podřízené hodnoty
                    const hasChildren = ciselnikValue.tempId && values.ciselnik.some(v => v.parentTempId === ciselnikValue.tempId);
                    if (hasChildren) {
                        await showInfo({
                            title: 'Odstranit hodnotu',
                            message: 'Nelze smazat hodnotu, která má podřízené hodnoty. Nejprve odstraňte nebo přesuňte podřízené hodnoty.',
                            confirmText: 'Zavřít',
                        });
                        return;
                    }
                    // Pokud má hodnota ID (existuje na serveru), zkontrolujeme, zda je použita v datech
                    if (ciselnikValue.id != null) {
                        const response = await AtributyApi.existsDataForCiselnik(ciselnikValue.id);
                        if (response.data) {
                            await showInfo({
                                title: 'Odstranit hodnotu',
                                message: 'Nelze smazat hodnotu, protože je použita u existujících objektů. Před smazáním je potřeba zajistit, aby hodnota nebyla nikde použita.',
                                confirmText: 'Zavřít',
                            });
                            return;
                        }
                    }
                    const result = await showConfirm({
                        title: 'Odstranit hodnotu',
                        message: 'Opravdu chcete odstranit hodnotu?',
                        intent: 'danger',
                        cancelText: 'Zrušit',
                        confirmText: 'Smazat',
                    });
                    if (result) {
                        const ciselnikNewValue = structuredClone(values.ciselnik);
                        ciselnikNewValue.splice(index, 1);
                        change('ciselnik', ciselnikNewValue);
                    }
                };

                return (
                    <Dialog size="md" onClose={onClose} title={isEdit ? 'Úprava atributu' : 'Nový atribut'}>
                        <DialogBody>
                            <Space vertical>
                                <Card mode={'edit'}>
                                    <Block>
                                        <VF.String label={'Název atributu'} name={'popisek'} required disabled={submitting} />
                                        <OnChange name="popisek">{value => change('nazev', value)}</OnChange>
                                        <VF.SelectBox
                                            disabled={isEdit || submitting}
                                            label={'Typ atributu'}
                                            name={'typ'}
                                            items={typItems}
                                            useIdAsValue
                                            required
                                        />
                                        {values.typ === EnumTypLayerAttr.TEXT && (
                                            <VF.SelectBox
                                                disabled={submitting}
                                                label={'Formátování hodnoty'}
                                                name={'interpretace'}
                                                items={getTextInterpretaceItems()}
                                                placeholder={'Prostý text'}
                                                useIdAsValue
                                                allowNullValue
                                            />
                                        )}
                                        {values.typ === EnumTypLayerAttr.STRING && (
                                            <VF.SelectBox
                                                disabled={submitting}
                                                label={'Formátování hodnoty'}
                                                name={'interpretace'}
                                                items={getStringInterpretaceItems()}
                                                placeholder={'Prostý text'}
                                                useIdAsValue
                                                allowNullValue
                                            />
                                        )}
                                        {values.typ === EnumTypLayerAttr.ENUM && (
                                            <VF.SelectBox
                                                disabled={submitting}
                                                label={'Počet hodnot'}
                                                name={'interpretace'}
                                                items={getCiselnikInterpretaceItems()}
                                                useIdAsValue
                                                required
                                            />
                                        )}
                                        {xsdTyp && (
                                            <WaitForQueryData queries={[externalAttributeQuery]}>
                                                {() => {
                                                    const data = externalAttributeQuery.data || [];
                                                    const selectedItem = data.find(item => item.nazev === values.nazevAtributXsd);
                                                    return (
                                                        <>
                                                            <VF.SelectBox
                                                                disabled={submitting}
                                                                label={'Název externího atributu'}
                                                                name={'nazevAtributXsd'}
                                                                allowNullValue
                                                                allowCreateItems
                                                                getNewItem={text => ({id: text, nazev: text})}
                                                                items={data.map(item => ({
                                                                    id: item.kod,
                                                                    nazev: item.nazev,
                                                                }))}
                                                                useIdAsValue
                                                            />
                                                            <VF.Autocomplete
                                                                disabled={submitting || !values.nazevAtributXsd}
                                                                label={'Výchozí hodnota externího atributu'}
                                                                name={'defaultniHodnota'}
                                                                allowCreateItems
                                                                onSearchItems={async text =>
                                                                    await actionApiCall(
                                                                        async () =>
                                                                            (
                                                                                await AtributyApi.getExternalDefaultValue(
                                                                                    xsdTyp,
                                                                                    undefined,
                                                                                    selectedItem?.kod,
                                                                                    text,
                                                                                )
                                                                            ).data,
                                                                    )
                                                                }
                                                                getItemName={(item: AttributeExternalValueVO | string) =>
                                                                    typeof item === 'string' ? item : item.nazev
                                                                }
                                                                getNewItem={text => text}
                                                                allowNullValue
                                                            />
                                                        </>
                                                    );
                                                }}
                                            </WaitForQueryData>
                                        )}
                                        <Row>
                                            <Col>
                                                <VF.Checkbox
                                                    label={'Povinný'}
                                                    name={'povinne'}
                                                    type={'checkbox'}
                                                    disabled={(xsdTyp && initialValues.povinne) || values.pouzitJakoLabel || submitting}
                                                />
                                            </Col>
                                            {xsdTyp && (
                                                <Col>
                                                    <VF.Checkbox
                                                        label={'Viditelný'}
                                                        name={'zobrazit'}
                                                        type={'checkbox'}
                                                        disabled={!values.nazevAtributXsd || submitting}
                                                    />
                                                </Col>
                                            )}
                                            <Col>
                                                <VF.Checkbox label={'Publikovat'} name={'publikovat'} type={'checkbox'} disabled={submitting} />
                                            </Col>
                                            <Col>
                                                <VF.Checkbox
                                                    label={'Použít atribut jako název'}
                                                    name={'pouzitJakoLabel'}
                                                    type={'checkbox'}
                                                    disabled={submitting}
                                                />
                                                <OnChange name="pouzitJakoLabel">
                                                    {value => {
                                                        if (value) {
                                                            change('povinne', true);
                                                        }
                                                    }}
                                                </OnChange>
                                            </Col>
                                        </Row>
                                        {(values.typ === EnumTypLayerAttr.STRING ||
                                            values.typ === EnumTypLayerAttr.INT ||
                                            values.typ === EnumTypLayerAttr.NUMBER ||
                                            values.typ === EnumTypLayerAttr.TEXT ||
                                            values.typ === EnumTypLayerAttr.ENUM) && (
                                            <Row>
                                                <Col>
                                                    <VF.Checkbox
                                                        label={'Vyhledávání dle hodnoty'}
                                                        name={'vyhledavatelny'}
                                                        type={'checkbox'}
                                                        disabled={submitting}
                                                    />
                                                </Col>
                                            </Row>
                                        )}
                                        {/* MCW-29581 – filtrování číselníku v mapě je součástí atlasu přístupnosti */}
                                        {values.typ === EnumTypLayerAttr.ENUM && atlasPristupnosti && (
                                            <Row>
                                                <Col>
                                                    <VF.Checkbox
                                                        label={'Filtrování v mapě'}
                                                        name={'filtrovatelny'}
                                                        type={'checkbox'}
                                                        disabled={submitting}
                                                    />
                                                </Col>
                                            </Row>
                                        )}
                                        {values.typ === EnumTypLayerAttr.DATE_TIME && (
                                            <Row>
                                                <Col>
                                                    <VF.Checkbox
                                                        label={'Vyplnit aktuálním datem'}
                                                        name={'vyplnitAktualniDatum'}
                                                        type={'checkbox'}
                                                        disabled={submitting}
                                                    />
                                                </Col>
                                            </Row>
                                        )}
                                        <OnChange name="typ">
                                            {(value) => {
                                                change('ciselnik', []);
                                                // u číselníku výchozí jednohodnotový, jinak bez interpretace
                                                change('interpretace', value === EnumTypLayerAttr.ENUM ? EnumInterpretace.CISELNIK : undefined);
                                            }}
                                        </OnChange>
                                    </Block>
                                </Card>
                                {values.typ === EnumTypLayerAttr.ENUM && (
                                    <Card
                                        mode={'edit'}
                                        title={'Hodnoty číselníku'}
                                        extra={
                                            <Button
                                                style="default"
                                                onClick={() => handleCreateUpdateHodnota()}
                                                disabled={submitting}
                                                icon={<Icon icon={mcNew} />}
                                            />
                                        }
                                    >
                                        <Block>
                                            {!values.ciselnik || values.ciselnik?.length === 0 ? (
                                                <NoData />
                                            ) : (
                                                <CiselnikTree
                                                    ciselnik={values.ciselnik}
                                                    onChange={updated => change('ciselnik', updated)}
                                                    onEdit={ciselikValue => handleCreateUpdateHodnota(ciselikValue, values.ciselnik.indexOf(ciselikValue))}
                                                    onRemove={ciselikValue => removeHodnota(values.ciselnik.indexOf(ciselikValue))}
                                                />
                                            )}
                                            {submitFailed && errors.ciselnik && values.ciselnik.length === 0 && <Text intent="danger">{errors.ciselnik}</Text>}
                                        </Block>
                                    </Card>
                                )}
                            </Space>
                        </DialogBody>
                        <DialogFooter>
                            <Button style="default" onClick={onClose} disabled={submitting}>
                                Zrušit
                            </Button>
                            <Button style="primary" onClick={handleSubmit} disabled={submitting}>
                                {isEdit ? 'Uložit' : 'Vytvořit'}
                            </Button>
                        </DialogFooter>
                    </Dialog>
                );
            }}
        </Form>
    );
};
